/**
 * The menu's DOM view and its input plumbing.
 *
 * This file draws `MenuModel` and feeds it `NavAction`s. It makes no decisions:
 * every branch about what a key does, what a row reads or where Escape goes
 * lives in the model, which is why the navigation can be tested without a
 * browser. If you find yourself adding an `if (screen === ...)` here that is not
 * about LAYOUT, it belongs in the model instead.
 *
 * The match itself is behind an interface (`MatchSession`), supplied by
 * src/main.ts. The menu therefore knows nothing about Three.js, the world, or
 * the tick loop — it starts, pauses, reconfigures and disposes an opaque
 * session. That is also what keeps main.ts's edit small and keeps this module
 * importable in Node.
 */

import {
  DIFFICULTY_CHOICES,
  MAX_BINDING_SLOTS,
  loadSettings,
  resolveDifficulty,
  saveSettings,
  teamSizeForMode,
  type BotDifficulty,
  type GameSettings,
  type StorageLike,
} from '../game/settings';
import { botRosterFor } from '../game/bots';
import { drillById, drillVerdict, type DrillSpec, type DrillSummary } from '../game/drills';
import type { Policy } from '../bot/bot';
import { CAMERA_SETTING_RANGES, type CameraSettings } from '../render/camera';
import { BINDING_ROWS } from './bindings';
import {
  MenuModel,
  SCREEN_TITLES,
  type MatchResult,
  type MenuEffect,
  type MenuItem,
  type Screen,
} from './menu-model';
import { GamepadNav, firstGamepad, navActionForKey } from './nav-input';
import { MENU_STYLE } from './style';
import { NetRuntime, type NetRuntimeOptions } from './net-runtime';
import { AccountRuntime, type AccountRuntimeOptions } from './account';
import { TEXT_FIELDS, displayTextValue, netStatusLine } from './net-model';
import { connectingPanelMarkup, disconnectPanelMarkup, lobbyPanelMarkup } from './net-panels';
import type { NetClient } from '../net/client';
import {
  TrainingRuntime,
  checkpointDetailMarkup,
  checkpointsPanelMarkup,
  trainingPanelMarkup,
} from './training';
import { ProgressionRuntime, type ProgressionOptions } from './progression';
import { RankedRuntime } from './ranked';
import { rankedPanelMarkup } from './ranked-panels';
import {
  garageLivery,
  garagePanelMarkup,
  presetPanelMarkup,
  slotPanelMarkup,
  stockLivery,
} from './garage';
import { GaragePreview } from '../render/garage-preview';
import type { CarLivery } from '../render/livery';
import {
  addPreset,
  equip,
  selectPreset,
  setLoadoutColors,
} from '../game/inventory';
import {
  challengePanelMarkup,
  passPanelMarkup,
  shopPanelMarkup,
  summaryMarkup,
} from './progression-panels';

// ---------------------------------------------------------------------------
// The seam to the game
// ---------------------------------------------------------------------------

/** A running match, from the menu's point of view. */
export interface MatchSession {
  /** Freeze the tick. Rendering continues, so the paused world is the backdrop. */
  setPaused(paused: boolean): void;
  /** Re-apply live settings: camera, ball cam, key bindings. */
  applySettings(settings: GameSettings): void;
  dispose(): void;
}

export interface MatchHooks {
  /** The world's kickoff countdown has finished; hand control to the player. */
  onCountdownComplete(): void;
  onMatchEnded(result: MatchResult): void;
  /**
   * The player asked for the pause menu from inside the match.
   *
   * Exists for exactly one caller: the on-screen pause button (see
   * src/input/touch.ts). A phone has no Escape key, and without this there is
   * no way off the pitch at all — not to the settings, not to the main menu,
   * not out of a multiplayer room. It is the same `back` the key dispatches,
   * so the two routes into the pause menu cannot diverge.
   */
  onPauseRequested(): void;
  /**
   * A practice drill has played its last attempt. Only a drill session ever
   * calls it; `onMatchEnded` is not reused because a drill has no score, no
   * winner and no progression award, and a `MatchResult` invented to carry a
   * hit count would be a lie the rest of the menu would have to believe.
   */
  onDrillFinished(summary: DrillSummary): void;
}

export interface SessionOptions {
  /**
   * Builds the learned policy for one bot-driven car. Present only when a
   * checkpoint exists; `botRosterFor` treats its absence as "the `learned` tier
   * is not available" and resolves back to a scripted one.
   */
  learnedPolicy?: (carIndex: number) => Policy;
  /**
   * The player's equipped loadout, already resolved to a renderable livery.
   *
   * Only the HUMAN's car wears it. Bots get the stock team car, which is not a
   * limitation so much as the point: a pitch where every car wore the player's
   * decal would make their own car harder to find, and the team colour is the
   * read that has to survive.
   */
  playerLivery?: CarLivery;
  /**
   * A live connection. Present exactly for a networked match, and its presence
   * is the ONLY difference between the two kinds of session: the renderer, the
   * HUD, the camera, the effects and the replay are the same objects either
   * way, driven by a `WorldView` that happens to be backed by a predicted world
   * instead of a local one. See `NetWorldView` in src/net/client.ts.
   */
  net?: NetClient;
  /**
   * A practice drill. Present exactly for a drill session, and its presence
   * turns the session into one: the world is built from `drillMatchConfig`,
   * one car, no opponent, and every attempt's ball is placed by the run rather
   * than by a kickoff. See src/game/drills.ts.
   */
  drill?: DrillSpec;
}

export interface MenuHooks {
  createSession(
    settings: GameSettings,
    hooks: MatchHooks,
    options?: SessionOptions
  ): MatchSession;
  /** Overridable for tests; defaults to localStorage. */
  storage?: StorageLike | null;
  /** Overridable for tests; the transport, timers and clipboard multiplayer uses. */
  net?: NetRuntimeOptions;
  /**
   * Overridable for tests; the profile store and the CLOCK the shop, pass and
   * challenges are read against. See the header of ./progression — that clock
   * is the only wall-clock read in the whole feature.
   */
  progression?: ProgressionOptions;
  /** Overridable for tests; the fetch and token store behind the account. */
  account?: AccountRuntimeOptions;
}

/** The training screens, which share a layout and a live watch session. */
const TRAINING_SCREENS: readonly Screen[] = ['training', 'checkpoints', 'checkpoint'];

/** Screens that show the idling car beside the rows, rather than a panel. */
const SHOWCASE_SCREENS: readonly Screen[] = ['welcome', 'main'];

/** The multiplayer screens, which get the wider two-column layout. */
const NET_PANEL_SCREENS: readonly Screen[] = ['lobby', 'connecting', 'disconnected'];

/** Tiers shown in the track panel. Odd, so the cursor sits in the middle.
 *  The progression screens get the same two-column layout the training and
 *  multiplayer ones do; that is pure CSS, keyed on `data-screen` in ./style. */
const PASS_TRACK_WIDTH = 9;

// ---------------------------------------------------------------------------
// Small render helpers
// ---------------------------------------------------------------------------

const CRUMB_LABELS: Record<Screen, string> = {
  welcome: 'Vroomball',
  signin: 'Sign In',
  register: 'Create Account',
  main: 'Menu',
  garage: 'Garage',
  garageSlot: 'Slot',
  garagePresets: 'Presets',
  mode: 'Playlist',
  setup: 'Setup',
  mutators: 'Mutators',
  camera: 'Camera',
  audio: 'Audio',
  controls: 'Controls',
  bind: 'Bind',
  text: 'Edit',
  ranked: 'Ranked',
  drills: 'Practice',
  drillresult: 'Drill',
  training: 'Training',
  checkpoints: 'Checkpoints',
  checkpoint: 'Detail',
  shop: 'Shop',
  rocketpass: 'Rocket Pass',
  challenges: 'Challenges',
  countdown: 'Kickoff',
  match: 'Match',
  pause: 'Paused',
  postmatch: 'Result',
  account: 'Account',
  multiplayer: 'Multiplayer',
  netmode: 'Quick Match',
  connecting: 'Connecting',
  lobby: 'Lobby',
  netmatch: 'Match',
  netpause: 'Menu',
  disconnected: 'Disconnected',
};

/** Escape the four characters that can break out of a text node or attribute. */
const esc = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** 0..1 position of `value` in a camera slider's range, for the fill and tick. */
const trackFraction = (value: number, min: number, max: number): number => {
  if (!(max > min)) return 0;
  return Math.min(1, Math.max(0, (value - min) / (max - min)));
};

const pct = (t: number): string => `${(t * 100).toFixed(2)}%`;

const clockLabel = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));
  return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, '0')}`;
};

const TEAM_NAMES = ['Blue', 'Orange'] as const;

const difficultyLabel = (difficulty: BotDifficulty): string =>
  DIFFICULTY_CHOICES.find(c => c.value === difficulty)?.label ?? '—';

/** Matches TEAM_COLORS in src/render/materials.ts; imported as text so this
 *  module stays free of the Three.js render layer. */
const TEAM_HEX = ['#2a7dff', '#ff8a1f'] as const;

// ---------------------------------------------------------------------------
// Overlay
// ---------------------------------------------------------------------------

export class MenuOverlay {
  readonly root: HTMLDivElement;
  readonly model: MenuModel;

  private readonly styleEl: HTMLStyleElement;
  private readonly crumbsEl: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly subEl: HTMLElement;
  private readonly extraEl: HTMLElement;
  private readonly rowsEl: HTMLElement;
  private readonly noteEl: HTMLElement;
  private readonly legendEl: HTMLElement;
  private readonly modalEl: HTMLElement;
  private readonly modalAction: HTMLElement;
  private readonly modalTitle: HTMLElement;
  private readonly modalHint: HTMLElement;

  private session: MatchSession | null = null;
  /**
   * The watch-live match: a spectated bot-vs-bot game driven by the policy the
   * trainer is currently producing, rendered behind the training screens.
   *
   * Kept apart from `session` because it is not the player's match — it is not
   * paused when the menu is up (that is the entire point), it never notifies
   * the model, and it must be torn down before a real match is built rather
   * than alongside it: two `App`s means two WebGL contexts, and a browser
   * allows only a handful before it starts dropping the oldest.
   */
  private watch: MatchSession | null = null;
  /**
   * The garage turntable. Null until the player first opens the garage, and
   * unmounted the moment they leave it — see `syncPreview`.
   */
  private preview: GaragePreview | null = null;
  private readonly training: TrainingRuntime;
  /**
   * The live connection, when there is one.
   *
   * Kept beside `training` rather than inside the model for the same reason: a
   * socket, a clipboard and a retry timer are none of them things `MenuModel`
   * may own if it is to stay constructible in Node. The model reads a mirror —
   * see `setNetView`.
   */
  private readonly net: NetRuntime;
  /**
   * The profile, the claim ledger and the clock behind the three progression
   * screens. Beside `net` and `training` for the same reason: storage and a
   * wall clock are neither of them things `MenuModel` may own.
   */
  private readonly progression: ProgressionRuntime;
  /**
   * The login session. Owns the token and the /api calls; feeds the profile
   * the server holds into `progression`, and its token into `net`'s Hello.
   */
  private readonly account: AccountRuntime;
  /** The ladder screen's fetches and their answers. See ./ranked. */
  private readonly ranked: RankedRuntime;
  private readonly pad = new GamepadNav();
  private padLoop: number | null = null;
  /** Edge latch for the Start button, which pauses even while the menu is hidden. */
  private startHeld = false;

  /** Signature of the rows currently in the DOM, so we only rebuild on change. */
  private rowSignature = '';
  private rowEls: HTMLElement[] = [];

  private readonly onKeyDown: (event: KeyboardEvent) => void;
  private readonly onPaste: (event: ClipboardEvent) => void;

  constructor(container: HTMLElement, settings: GameSettings, private readonly hooks: MenuHooks) {
    this.model = new MenuModel(settings);
    this.training = new TrainingRuntime(settings, {
      changed: () => this.onTrainingChanged(),
      toast: text => {
        this.model.toast = text;
      },
    });

    this.net = new NetRuntime(
      settings,
      {
        changed: () => {
          this.model.setNetView(this.net.view);
          this.render();
        },
        navigate: event => {
          this.model.setNetView(this.net.view);
          this.model.notifyNet(event);
          // The stored room code moves when a room is joined, and the address
          // and name move from the prompt; persisting on every navigation is
          // cheap and means a crash never costs the code.
          this.persist();
          this.render();
        },
        matchStarted: client => this.startNetSession(client),
        matchEnded: () => this.disposeSession(),
      },
      {
        // Read per dial, so a sign-in that happened after boot is picked up.
        // The server kicks a Hello without a valid token; the account IS the
        // multiplayer identity. Deliberately before any test override spread —
        // a test that injects its own supplier wins.
        authToken: () => this.account.token,
        ...(hooks.net ?? {}),
      }
    );

    this.progression = new ProgressionRuntime(
      {
        changed: () => {
          this.model.setProgression(this.progression.state());
          this.render();
        },
        toast: text => {
          this.model.toast = text;
        },
      },
      hooks.progression ?? {}
    );
    // Synchronous: the anonymous starter profile is built in memory, so the
    // garage preview has something to draw from the first frame. The real
    // profile arrives from the server once the account resumes below.
    this.model.setProgression(this.progression.state());

    this.account = new AccountRuntime(
      settings,
      {
        changed: () => {
          this.model.setAccount(this.account.view);
          this.render();
        },
        // The server's profile is the profile. Adopt it, and relay every
        // future local mutation back — that is the whole authority story.
        onSession: session => {
          this.progression.adoptServer(session.profile, session.claims);
          this.model.setAccount(this.account.view);
          this.model.setProgression(this.progression.state());
          this.render();
        },
        onSignedOut: () => {
          this.progression.resetLocal();
          // One account's standing and history must not survive into the next.
          this.ranked.clear();
          this.model.setAccount(this.account.view);
          this.model.setProgression(this.progression.state());
          this.render();
        },
        toast: text => {
          this.model.toast = text;
          this.render();
        },
      },
      hooks.account ?? {}
    );
    this.ranked = new RankedRuntime({
      api: this.account.client,
      token: () => this.account.token,
      userId: () => this.account.userId,
      onChanged: () => this.render(),
    });

    this.progression.setRemote({ push: op => this.account.push(op) });
    this.model.setAccount(this.account.view);
    // Try the stored token. Answers asynchronously through the hooks above.
    this.account.resume();

    this.styleEl = document.createElement('style');
    this.styleEl.textContent = MENU_STYLE;
    document.head.appendChild(this.styleEl);

    this.root = document.createElement('div');
    this.root.className = 'rw-menu';
    this.root.innerHTML = `
      <div class="rw-menu-bg"></div>
      <div class="rw-menu-frame">
        <header class="rw-brand">
          <div class="rw-brand-mark">Vroom<span>ball</span></div>
          <div class="rw-brand-rule"></div>
          <div class="rw-brand-build">120 Hz · Soccar</div>
        </header>
        <main class="rw-panel">
          <div class="rw-crumbs"></div>
          <h1 class="rw-title"></h1>
          <div class="rw-sub"></div>
          <div class="rw-extra"></div>
          <div class="rw-rows"></div>
          <div class="rw-note"></div>
        </main>
        <footer class="rw-legend"></footer>
      </div>
      <div class="rw-modal" hidden>
        <div class="rw-modal-card">
          <div class="rw-modal-action"></div>
          <div class="rw-modal-title">Press a key</div>
          <div class="rw-modal-hint"></div>
        </div>
      </div>
    `;
    container.appendChild(this.root);

    const q = <T extends Element>(selector: string): T => {
      const el = this.root.querySelector(selector);
      if (!el) throw new Error(`menu element missing: ${selector}`);
      return el as T;
    };
    this.crumbsEl = q('.rw-crumbs');
    this.titleEl = q('.rw-title');
    this.subEl = q('.rw-sub');
    this.extraEl = q('.rw-extra');
    this.rowsEl = q('.rw-rows');
    this.noteEl = q('.rw-note');
    this.legendEl = q('.rw-legend');
    this.modalEl = q('.rw-modal');
    this.modalAction = q('.rw-modal-action');
    this.modalTitle = q('.rw-modal-title');
    this.modalHint = q('.rw-modal-hint');

    this.onKeyDown = event => this.handleKey(event);
    window.addEventListener('keydown', this.onKeyDown);
    // A server address is far more often pasted than typed, and there is no
    // real `<input>` here to receive it — the prompt is a rendered buffer.
    this.onPaste = event => {
      if (this.model.screen !== 'text') return;
      const text = event.clipboardData?.getData('text') ?? '';
      if (text.length === 0) return;
      event.preventDefault();
      this.apply(this.model.captureTextPaste(text));
    };
    window.addEventListener('paste', this.onPaste);
    this.rowsEl.addEventListener('click', event => this.handleClick(event));

    this.startPadLoop();
    // The store is asynchronous, so the first read cannot gate the boot. It
    // lands as a `changed` callback, which is also what unlocks the `learned`
    // tier if something was trained in a previous session.
    this.training.refresh();
    this.render();
  }

  /**
   * The trainer, the store or the active policy moved.
   *
   * The model is told, rather than reaching into the runtime itself, so that
   * everything the menu decides still depends only on the model — including
   * whether `learned` is selectable, which is the one setting whose
   * availability is a runtime fact.
   */
  private onTrainingChanged(): void {
    this.model.setTrainingPhase(this.training.phase, this.training.iteration);
    this.model.setCheckpoints(this.training.checkpoints);

    const available = this.training.learnedAvailable;
    if (this.model.learnedAvailable !== available) {
      this.model.learnedAvailable = available;
      const setup = this.model.settings.setup;

      if (available) {
        // Boot loaded the settings pessimistically — see the note at the bottom
        // of src/main.ts — so a player who last played against their trained bot
        // has had that scrubbed to a scripted tier. Now that the store has
        // answered, re-read it under the flag and put their choice back. Only
        // the tier is taken: everything else in memory is newer than the file.
        const stored = loadSettings(this.hooks.storage, true).settings;
        if (stored.setup.difficulty === 'learned') setup.difficulty = 'learned';
      } else {
        // The tier has just lost its policy — the last checkpoint deleted. It
        // must not survive in the stored setup, where a build that trusts it
        // would read it back. `resolveDifficulty` is the same scrub
        // `sanitizeMatchSetup` performs on load.
        setup.difficulty = resolveDifficulty(setup.difficulty, false);
      }
      this.persist();
    }

    this.syncWatch();
    this.render();
  }

  private persist(): void {
    saveSettings(this.model.settings, this.hooks.storage, this.model.learnedAvailable);
  }

  /**
   * Build the render session for a networked match.
   *
   * The SAME factory a local match goes through, with one extra option. That is
   * the whole integration: `App` reads `options.net` and takes its `WorldView`
   * from the prediction instead of from a local `World`, and every other thing
   * it builds — scene, camera, HUD, effects, replay buffer — is untouched.
   */
  private startNetSession(client: NetClient): void {
    // Before, not after: the watch match owns a WebGL context and the new one
    // is about to ask for a second. Same reason as `start-match`.
    this.disposeWatch();
    this.disposeSession();
    this.session = this.hooks.createSession(
      this.model.settings,
      {
        onCountdownComplete: () => this.render(),
        onMatchEnded: result => {
          this.model.notify('match-ended', result);
          this.render();
        },
        onPauseRequested: () => this.requestPause(),
        onDrillFinished: () => {},
      },
      { net: client }
    );
  }

  // -------------------------------------------------------------------------
  // Input
  // -------------------------------------------------------------------------

  private handleKey(event: KeyboardEvent): void {
    // The bind prompt owns the keyboard entirely: any key that reaches the page
    // while it is open is a candidate binding, not a shortcut.
    if (this.model.screen === 'bind') {
      event.preventDefault();
      this.apply(this.model.captureBindKey(event.code));
      return;
    }
    // So does the text prompt, and it has to: `Space` and `Backspace` are both
    // `NavAction`s, and both are ordinary characters in an address.
    if (this.model.screen === 'text') {
      // Ctrl/Cmd chords are left alone so the browser's own paste still runs;
      // it arrives on the `paste` listener below.
      if (!event.ctrlKey && !event.metaKey) event.preventDefault();
      this.apply(this.model.captureTextKey(event));
      return;
    }

    const action = navActionForKey(event.code);
    if (!action) return;

    // While the match is running the pad and keyboard belong to the car; only
    // Escape is intercepted, and only to open the pause menu.
    if (!this.model.overlayVisible && action !== 'back') return;

    event.preventDefault();
    this.apply(this.model.dispatch(action));
  }

  /**
   * Open the pause menu from inside a match.
   *
   * The SAME dispatch the Escape key performs — see `handleKey`, and the note
   * on `pause` in the header of ./menu-model.ts for why `back` pushes rather
   * than pops during play. Routing the button through `dispatch` rather than
   * setting a screen directly is what keeps the model the only writer, so the
   * touch route cannot end up in a state the keyboard route cannot reach.
   *
   * Ignored while the overlay is already up: the button is hidden with the
   * controls when the menu opens, but a release that lands in the same frame
   * would otherwise arrive after it and pop the screen the player just opened.
   */
  private requestPause(): void {
    if (this.model.overlayVisible) return;
    this.apply(this.model.dispatch('back'));
  }

  private handleClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const row = target?.closest<HTMLElement>('.rw-row');
    if (!row) return;
    const index = Number(row.dataset.index);
    if (!Number.isInteger(index)) return;

    // Clicking a row focuses it first, so a click and an Enter do the same
    // thing to the same row — the two input models never disagree.
    this.focusRow(index);
    const nav = target?.dataset.nav;
    this.apply(this.model.dispatch(nav === 'left' ? 'left' : nav === 'right' ? 'right' : 'confirm'));
  }

  /** Move focus to `index` by dispatching, so the model stays the only writer. */
  private focusRow(index: number): void {
    let guard = this.model.items.length;
    while (this.model.focusIndex !== index && guard-- > 0) this.model.dispatch('down');
  }

  private startPadLoop(): void {
    const frame = (): void => {
      this.pollPad();
      this.padLoop = requestAnimationFrame(frame);
    };
    this.padLoop = requestAnimationFrame(frame);
  }

  private pollPad(): void {
    const pad = firstGamepad();

    if (!this.model.overlayVisible) {
      // Mid-match the sticks and face buttons are driving. Start (button 9) is
      // the only menu input, and it has to be edge-detected here because
      // GamepadNav is deliberately not running.
      const down = pad?.buttons[9]?.pressed === true;
      if (down && !this.startHeld) this.apply(this.model.dispatch('back'));
      this.startHeld = down;
      this.pad.reset();
      return;
    }

    this.startHeld = pad?.buttons[9]?.pressed === true;
    const actions = this.pad.poll(pad, performance.now() / 1000);
    if (actions.length === 0) return;

    // Neither prompt can be driven by a pad — src/input/controller.ts uses a
    // fixed pad layout, and a pad has no letters — so B/Start only cancel out.
    if (this.model.screen === 'bind' || this.model.screen === 'text') {
      if (actions.includes('back')) this.apply(this.model.dispatch('back'));
      return;
    }
    for (const action of actions) this.apply(this.model.dispatch(action));
  }

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------

  private apply(effects: readonly MenuEffect[]): void {
    const selected = (): string | null => this.model.selectedCheckpointId;

    for (const effect of effects) {
      switch (effect) {
        case 'settings-changed':
          this.persist();
          this.session?.applySettings(this.model.settings);
          // Through the same override the watch match was BUILT from, not the
          // raw settings: the backdrop has no on-screen controls, and handing
          // it the player's would give it a set the moment they touched any
          // row on any screen.
          this.watch?.applySettings(this.watchSettings());
          break;
        case 'start-match':
        case 'restart-match':
          // The last match's award belongs to the last match. Left in place it
          // would be shown again at the end of this one, with numbers that
          // describe a game the player has already been paid for.
          this.progression.clearSummary();
          // Before, not after: the watch match and the garage turntable each
          // own a WebGL context and the new one is about to ask for another.
          this.disposeWatch();
          this.preview?.unmount();
          this.disposeSession();
          this.session = this.hooks.createSession(
            this.model.settings,
            {
              onCountdownComplete: () => {
                this.model.notify('countdown-complete');
                this.render();
              },
              onMatchEnded: result => {
                this.awardProgression(result);
                this.model.notify('match-ended', result);
                this.render();
              },
              onPauseRequested: () => this.requestPause(),
              onDrillFinished: () => {},
            },
            this.sessionOptions()
          );
          break;

        case 'start-drill':
          this.startDrill();
          break;

        case 'ranked-refresh':
          this.ranked.refresh(this.model.rankedMode);
          break;
        case 'quit-match':
          this.disposeSession();
          break;

        case 'training-start':
          this.training.start();
          break;
        case 'training-pause':
          this.training.pause();
          break;
        case 'training-resume':
          this.training.resume();
          break;
        case 'training-stop':
          this.training.stop();
          break;
        // The next run reads the config; a live one already read it, and the
        // rows that would change it are locked while it runs.
        case 'training-config-changed':
        // Handled by `syncWatch` below, which is called on every apply.
        case 'training-watch-changed':
          break;

        case 'checkpoints-refresh':
          this.training.refresh();
          break;
        case 'checkpoint-save':
          this.training.save();
          break;
        case 'checkpoint-import':
          this.training.importFile();
          break;
        case 'checkpoint-load': {
          const id = selected();
          if (id !== null) this.training.load(id);
          break;
        }
        case 'checkpoint-export': {
          const id = selected();
          if (id !== null) this.training.exportFile(id);
          break;
        }
        case 'checkpoint-delete': {
          const id = selected();
          if (id !== null) this.training.remove(id);
          break;
        }

        case 'net-connect': {
          const intent = this.model.netIntent;
          // A live watch match holds a WebGL context and a worker pool, and a
          // networked match is about to want both.
          this.disposeWatch();
          if (intent !== null) this.net.connect(intent);
          break;
        }
        case 'net-cancel':
          this.net.disconnect();
          break;
        case 'net-ready':
          this.net.toggleReady();
          break;
        case 'net-team':
          this.net.switchTeam();
          break;
        case 'net-leave':
          this.net.leave();
          break;
        case 'net-copy-code':
          this.net.copyCode();
          break;
        case 'net-reconnect':
          this.net.reconnect();
          break;

        // Every progression effect goes through the same two lines: ask the
        // runtime, then say what happened. The runtime owns the refusal
        // wording because it is the only thing that knows what the profile
        // actually had at the moment the row was pressed.
        case 'progression-refresh':
          this.progression.refresh();
          this.model.setProgression(this.progression.state());
          break;

        case 'loadout-changed':
          this.applyGarageEdit();
          this.model.setProgression(this.progression.state());
          break;
        case 'shop-buy': {
          const row = this.model.shopSelection;
          if (row !== null) this.report(this.progression.buy(row.entry));
          break;
        }
        case 'pass-claim-free':
          this.report(this.progression.claimTier(this.model.passTier, 'free'));
          break;
        case 'pass-claim-premium':
          this.report(this.progression.claimTier(this.model.passTier, 'premium'));
          break;
        case 'pass-claim-all':
          this.report(this.progression.claimAllTiers());
          break;
        case 'pass-unlock':
          this.report(this.progression.unlockPremium());
          break;
        case 'challenge-claim': {
          const id = this.model.challengeSelection;
          if (id !== null) this.report(this.progression.claimChallenge(id));
          break;
        }
        case 'challenge-claim-all':
          this.report(this.progression.claimAllChallenges());
          break;

        // The account effects hand the model's form to the runtime; answers
        // come back through the hooks wired in the constructor.
        case 'account-login':
          this.account.login(this.model.accountEmail, this.model.accountPassword);
          break;
        case 'account-register':
          this.account.register(
            this.model.accountEmail,
            this.model.accountName,
            this.model.accountPassword
          );
          break;
        case 'account-resend':
          this.account.resendVerification(this.model.accountEmail, this.model.accountPassword);
          break;
        case 'account-forgot':
          this.account.requestPasswordReset(this.model.accountEmail);
          break;
        case 'account-logout':
          this.account.logout();
          break;
      }
    }
    this.session?.setPaused(!this.model.simRunning);
    this.syncWatch();
    this.render();
  }

  /**
   * Pay the player for the match that just finished.
   *
   * A networked result carries no stats — see `MatchResult.stats` — so nothing
   * is awarded for one. That is deliberate and it is the honest reading of a
   * client that cannot be trusted with its own numbers: better no progression
   * than progression a player can fabricate by editing a tab.
   */
  private awardProgression(result: MatchResult): void {
    if (result.stats === undefined) return;
    this.progression.recordMatch(
      result.stats,
      result.winner === result.playerTeam,
      this.model.settings.setup.mode
    );
    this.model.setProgression(this.progression.state());
  }

  /**
   * Show what a purchase or a claim did, and re-read the mirror.
   *
   * A refusal is a toast rather than silence for the same reason a locked row
   * carries a note: "nothing happened" on a button is the one outcome a player
   * will assume is a bug.
   */
  private report(result: { ok: boolean; message: string }): void {
    this.model.toast = result.message;
    this.model.setProgression(this.progression.state());
  }

  /** What a match is built with. Absent factory means the tier is unavailable,
   *  which is exactly how `botRosterFor` reads it. */
  private sessionOptions(refreshable = false): SessionOptions {
    const factory = this.training.learnedPolicyFactory(refreshable);
    return {
      ...(factory === undefined ? {} : { learnedPolicy: factory }),
      // The loadout for the side the player is ON. `setup.team` and not
      // `garageTeam`: which loadout you are EDITING and which one you are
      // WEARING are different questions, and only the second one is the match's.
      playerLivery: this.playerLivery(),
    };
  }

  /**
   * The player's livery for the team they are about to play on.
   *
   * Also what decides which BODY they drive: a garage that let you equip a
   * Dominus and then put you in an Octane would be a garage that does not work.
   * `settings.setup.car` is kept in step so the Match Setup screen's Car row
   * agrees with it rather than quietly disagreeing.
   */
  private playerLivery(): CarLivery | undefined {
    const state = this.model.progression;
    if (state === null) return undefined;
    const livery = garageLivery(state.profile, this.model.settings.setup.team);
    if (this.model.settings.setup.car !== livery.body) {
      this.model.settings.setup.car = livery.body;
      this.persist();
    }
    return livery;
  }

  /**
   * Bring the turntable into line with where the player is.
   *
   * Built on arrival at a garage screen and destroyed on the way out, because a
   * WebGL context is a scarce resource and the match is entitled to one. The
   * host element is recreated by every `renderExtra`, so `mount` is idempotent
   * for the same host and this can safely be called on every render.
   */
  private syncPreview(): void {
    const state = this.model.progression;
    const host = this.extraEl.querySelector<HTMLElement>('[data-garage-preview]');
    if (host === null) {
      this.preview?.unmount();
      return;
    }
    // On the front screen the car shown is the one a match would put you in:
    // the loadout for the team you are set to play on — or the stock team car
    // when nobody is signed in, which is also what a match would do.
    const showcase = SHOWCASE_SCREENS.includes(this.model.screen);
    const team = showcase ? this.model.settings.setup.team : this.model.garageTeam;
    if (state === null && !showcase) {
      this.preview?.unmount();
      return;
    }
    if (this.preview === null) this.preview = new GaragePreview();
    this.preview.mount(host);
    this.preview.setLivery(
      team,
      state === null ? stockLivery(team) : garageLivery(state.profile, team)
    );
  }

  /**
   * Apply what a garage row asked for.
   *
   * The MODEL records the request and this applies it, for the same reason the
   * shop works that way: `ProgressionRuntime` owns the profile and is the only
   * thing allowed to write one, which is what keeps the exhaustive navigation
   * search out of localStorage.
   */
  private applyGarageEdit(): void {
    const edit = this.model.garageEdit;
    this.model.garageEdit = null;
    if (edit === null) return;

    const profile = this.progression.current;
    const next =
      edit.kind === 'equip'
        ? equip(profile, edit.team, edit.slot, edit.instanceId)
        : edit.kind === 'colors'
          ? setLoadoutColors(profile, edit.team, edit.primary, edit.accent)
          : edit.kind === 'preset'
            ? selectPreset(profile, edit.id)
            : addPreset(profile);
    if (next !== profile) this.progression.applyProfile(next);
  }

  /**
   * Bring the watch-live match into line with where the player is.
   *
   * Wanted only on the training screens, only with the toggle on, only with a
   * policy to drive it, and never while the player's own match exists. All four
   * are re-checked here rather than at the point each one changes, because the
   * conditions move independently and a lifecycle spread over four call sites
   * is how you end up with two of these running at once.
   */
  private syncWatch(): void {
    const want =
      TRAINING_SCREENS.includes(this.model.screen) &&
      this.model.settings.training.watchLive &&
      this.training.learnedAvailable &&
      this.session === null;

    if (!want) {
      this.disposeWatch();
      return;
    }
    if (this.watch === null) {
      this.watch = this.hooks.createSession(
        this.watchSettings(),
        // The watch match is a backdrop behind a menu that is already open —
        // there is nothing to pause, and nothing draws a button to ask.
        {
          onCountdownComplete: () => {},
          onMatchEnded: () => {},
          onPauseRequested: () => {},
          onDrillFinished: () => {},
        },
        this.sessionOptions(true)
      );
    }
    // Never paused: `simRunning` is false on a menu screen, and a frozen
    // demonstration of a policy learning is no demonstration at all.
    this.watch.setPaused(false);
  }

  /**
   * The player's settings with the match forced into a spectated bot-vs-bot
   * game that does not end. Their camera and bindings are kept — it is their
   * screen, and a demo that ignores their field of view looks broken.
   */
  /**
   * Build and start a practice session for `model.selectedDrillId`.
   *
   * The SAME `createSession` a match goes through, with one extra option —
   * exactly as multiplayer is one extra option. What makes it a drill is
   * `SessionOptions.drill`, and everything else the session builds (scene,
   * camera, HUD, effects, the on-screen controls) is the same object graph.
   *
   * A drill that no longer exists — an id from a build where it did — pops
   * back to the list rather than starting a session with nothing in it.
   */
  private startDrill(): void {
    const spec = this.model.selectedDrillId === null ? undefined : drillById(this.model.selectedDrillId);
    if (spec === undefined) {
      this.model.selectedDrillId = null;
      return;
    }
    this.disposeWatch();
    this.preview?.unmount();
    this.disposeSession();
    this.session = this.hooks.createSession(
      this.drillSettings(),
      {
        onCountdownComplete: () => {
          this.model.notify('countdown-complete');
          this.render();
        },
        // A drill has no full time. It cannot reach the clock (see the duration
        // in `drillMatchConfig`) and it has no opponent to lose to.
        onMatchEnded: () => {},
        onPauseRequested: () => this.requestPause(),
        onDrillFinished: summary => {
          this.model.drillSummary = summary;
          this.model.notify('drill-finished');
          this.render();
        },
      },
      { ...this.sessionOptions(), drill: spec }
    );
  }

  /**
   * The settings a drill session is built from.
   *
   * Free play on blue, whatever the player last set up for a match. The mode
   * is what gives the drill its ONE car: `botRosterFor` builds a single-car
   * roster for free play, and a drill with an opponent in it would be a drill
   * where the ball is wherever the opponent put it — which is the exact
   * problem drills exist to solve. Blue, because every shot in the catalogue
   * is authored shooting towards +Y.
   */
  private drillSettings(): GameSettings {
    return {
      ...this.model.settings,
      setup: { ...this.model.settings.setup, mode: 'freeplay', team: 0 },
    };
  }

  private watchSettings(): GameSettings {
    return {
      ...this.model.settings,
      setup: {
        ...this.model.settings.setup,
        mode: 'bot-showcase',
        difficulty: 'learned',
        length: 'unlimited',
        overtime: false,
      },
      // Nobody is driving and the menu is on top of it. Left at the player's
      // setting, the backdrop would build a second set of on-screen controls
      // — a stick and five buttons sitting UNDER the menu, lit up by every
      // press meant for a row above them.
      touch: { ...this.model.settings.touch, mode: 'off' },
    };
  }

  private disposeWatch(): void {
    if (this.watch === null) return;
    this.watch.dispose();
    this.watch = null;
    this.training.releaseWatchPolicies();
  }

  private disposeSession(): void {
    this.session?.dispose();
    this.session = null;
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  render(): void {
    const model = this.model;
    const screen = model.screen;
    const visible = model.overlayVisible;

    this.root.hidden = !visible;
    this.root.dataset.team = String(model.settings.setup.team);
    if (!visible) return;

    // What is drawn and what is focused can differ: the bind prompt is a modal
    // over the controls list, so the list stays on screen behind it.
    const painted = model.paintScreen;
    // The training screens are two-column and much wider, and the backdrop
    // thins out when there is a live match behind it worth seeing. Both are
    // pure layout, so both are a data attribute rather than a branch in here.
    this.root.dataset.screen = painted;
    if (this.watch !== null) this.root.dataset.watching = 'on';
    else delete this.root.dataset.watching;
    const items = model.itemsFor(painted);
    const focus = model.focusIndexFor(painted);

    const titles = SCREEN_TITLES[painted];
    this.crumbsEl.innerHTML = model.breadcrumb
      .map((s, i) =>
        i === model.breadcrumb.length - 1
          ? `<b>${esc(CRUMB_LABELS[s])}</b>`
          : esc(CRUMB_LABELS[s])
      )
      .join(' &nbsp;›&nbsp; ');
    this.titleEl.textContent = titles.title;
    // The multiplayer screens describe a moving thing, so their subtitle is the
    // live status line rather than a fixed one — and it comes from the single
    // definition in ./net-model so the lobby, the connect screen and the
    // disconnect message cannot describe one state three ways.
    // The account screens do the same: their subtitle is what the last request
    // actually did, in a colour that says whether it worked. Without it the
    // only feedback registering ever produced was a row note that existed for
    // as long as the request was in flight.
    const account = model.accountStatus;
    this.subEl.textContent = NET_PANEL_SCREENS.includes(painted)
      ? netStatusLine(model.net)
      : (account?.text ?? titles.sub);
    this.subEl.classList.toggle('bad', account?.tone === 'error');
    this.subEl.classList.toggle('good', account?.tone === 'success');

    this.renderExtra(painted);
    this.renderRows(items, focus);
    this.renderNote(items[focus] ?? null);
    this.renderLegend(screen);
    this.renderModal();
    // After `renderExtra`, which is what creates the element the turntable
    // lives in — and on every render, because that element is rebuilt whenever
    // a row's value changes.
    this.syncPreview();
  }

  private renderRows(items: readonly MenuItem[], focus: number): void {
    const signature = items.map(i => `${i.kind}:${i.id}:${i.enabled}`).join('|');
    if (signature !== this.rowSignature) {
      this.rowSignature = signature;
      this.rowsEl.innerHTML = items.map((item, i) => this.rowMarkup(item, i)).join('');
      this.rowEls = [...this.rowsEl.querySelectorAll<HTMLElement>('.rw-row')];
    } else {
      // Same rows, changed values: patch rather than rebuild so the focus bar's
      // transition is not restarted on every keypress.
      items.forEach((item, i) => {
        const el = this.rowEls[i];
        if (el) el.innerHTML = this.rowInner(item);
      });
    }

    this.rowEls.forEach((el, i) => el.classList.toggle('on', i === focus));
    this.rowEls[focus]?.scrollIntoView({ block: 'nearest' });
  }

  private rowMarkup(item: MenuItem, index: number): string {
    const classes = ['rw-row'];
    if (!item.enabled) classes.push('locked');
    if (item.kind === 'action' && item.emphasis) classes.push('hero');
    const columns =
      item.kind === 'slider'
        ? // Wide enough for both chevrons plus the 108px value column; any
          // narrower and the left chevron is squeezed out of the row.
          '1fr 168px 150px'
        : item.kind === 'bind'
          ? `1fr repeat(${MAX_BINDING_SLOTS}, auto)`
          : item.kind === 'text'
            ? // A URL is long. The label gets a fixed share and the value takes
              // the rest, ellipsized, rather than the label wrapping.
              'minmax(0, 200px) 1fr'
            : '1fr auto';
    return `<div class="${classes.join(' ')}" data-index="${index}" style="grid-template-columns:${columns}">${this.rowInner(item)}</div>`;
  }

  private rowInner(item: MenuItem): string {
    const label = `<div class="rw-row-label">${esc(item.label)}</div>`;
    const lock = item.enabled ? '' : `<span class="rw-lock">${esc(item.lockLabel ?? 'Soon')}</span>`;

    switch (item.kind) {
      case 'action':
        return `${label}<div class="rw-row-value">${lock}</div>`;

      case 'value': {
        // No chevrons, deliberately: a price has no adjacent price. The bar
        // sits under the label so a "3 / 10" row reads as a counter and not as
        // a setting that happens to contain a slash.
        const bar =
          item.fraction === undefined
            ? ''
            : `<div class="rw-mini"><i style="width:${pct(item.fraction)}"></i></div>`;
        const badge =
          item.badge === undefined
            ? ''
            : `<span class="rw-badge"${
                item.badgeTone === undefined ? '' : ` style="--tone:${esc(item.badgeTone)}"`
              }>${esc(item.badge)}</span>`;
        // A painted variant gets its own colour as a dot beside the name: the
        // one thing about an item a word cannot carry.
        const swatch =
          item.swatch === undefined
            ? ''
            : `<i class="rw-dot" style="background:${esc(item.swatch)}"></i>`;
        return (
          `<div class="rw-row-stack">` +
          (swatch === '' ? label : `<div class="rw-row-head">${swatch}${label}</div>`) +
          `${bar}</div>` +
          `<div class="rw-row-value">${lock}${badge}<b>${esc(item.valueLabel)}</b></div>`
        );
      }

      case 'choice':
        return `${label}${this.valueMarkup(item.valueLabel, lock)}`;

      case 'toggle':
        return `${label}${this.valueMarkup(item.on ? 'On' : 'Off', lock)}`;

      case 'slider': {
        const t = trackFraction(item.value, item.min, item.max);
        const key = item.id.slice('cam:'.length) as keyof CameraSettings;
        const range = CAMERA_SETTING_RANGES[key];
        const defaultT = range ? trackFraction(range.default, item.min, item.max) : 0;
        const track =
          `<div class="rw-track"><i style="width:${pct(t)}"></i>` +
          `<u style="left:${pct(defaultT)}"></u></div>`;
        return `${label}${track}${this.valueMarkup(item.valueLabel, lock)}`;
      }

      case 'bind': {
        const caps = item.slots
          .map((cap, slot) => {
            const cls = ['rw-cap'];
            if (cap === '—') cls.push('empty');
            if (slot === item.slot) cls.push('armed');
            return `<div class="${cls.join(' ')}">${esc(cap)}</div>`;
          })
          .join('');
        return `${label}${caps}`;
      }

      case 'text': {
        // No chevrons: there is no previous or next address, and a pair of
        // arrows that do nothing is the same lie as an enabled locked row.
        const shown = item.empty ? 'Not set' : item.value;
        return (
          `${label}<div class="rw-row-value">${lock}` +
          `<b class="rw-text${item.empty ? ' empty' : ''}">${esc(shown)}</b>` +
          `<span class="rw-pen">Edit</span></div>`
        );
      }
    }
  }

  private valueMarkup(value: string, lock: string): string {
    return (
      `<div class="rw-row-value">${lock}` +
      `<span class="rw-chev" data-nav="left">‹</span>` +
      `<b>${esc(value)}</b>` +
      `<span class="rw-chev" data-nav="right">›</span></div>`
    );
  }

  private renderNote(focused: MenuItem | null): void {
    const toast = this.model.toast;
    const note = toast || focused?.note || '';
    this.noteEl.textContent = note;
    this.noteEl.classList.toggle('warn', toast.length > 0);
  }

  private renderExtra(screen: Screen): void {
    const progression = this.model.progression;
    if (screen === 'postmatch') {
      this.extraEl.innerHTML =
        this.scoreboardMarkup() + summaryMarkup(this.progression.lastSummary);
    } else if (screen === 'shop') {
      this.extraEl.innerHTML = progression === null ? '' : shopPanelMarkup(progression);
    } else if (screen === 'rocketpass') {
      this.extraEl.innerHTML =
        progression === null
          ? ''
          : passPanelMarkup(progression, this.model.passTier, PASS_TRACK_WIDTH);
    } else if (screen === 'challenges') {
      this.extraEl.innerHTML = progression === null ? '' : challengePanelMarkup(progression);
    } else if (screen === 'garage') {
      this.extraEl.innerHTML =
        progression === null ? '' : garagePanelMarkup(progression.profile, this.model.garageTeam);
    } else if (screen === 'garageSlot') {
      const slot = this.model.garageSlot;
      this.extraEl.innerHTML =
        progression === null || slot === null
          ? ''
          : slotPanelMarkup(progression.profile, this.model.garageTeam, slot);
    } else if (screen === 'garagePresets') {
      this.extraEl.innerHTML = progression === null ? '' : presetPanelMarkup(progression.profile);
    } else if (screen === 'ranked') {
      this.extraEl.innerHTML = rankedPanelMarkup(this.ranked.view());
    } else if (screen === 'drillresult') {
      this.extraEl.innerHTML = this.drillResultMarkup();
    } else if (screen === 'setup' || screen === 'countdown') {
      this.extraEl.innerHTML = this.matchCardMarkup();
    } else if (screen === 'training') {
      this.extraEl.innerHTML = trainingPanelMarkup(this.training.view());
    } else if (screen === 'checkpoints') {
      this.extraEl.innerHTML = checkpointsPanelMarkup(this.model.checkpoints);
    } else if (screen === 'checkpoint') {
      this.extraEl.innerHTML = checkpointDetailMarkup(this.model.selectedCheckpoint);
    } else if (screen === 'lobby') {
      this.extraEl.innerHTML = lobbyPanelMarkup(this.model.net);
    } else if (screen === 'connecting') {
      this.extraEl.innerHTML = connectingPanelMarkup(
        this.model.net,
        this.model.settings.multiplayer.serverAddress
      );
    } else if (screen === 'disconnected') {
      this.extraEl.innerHTML = disconnectPanelMarkup(this.model.net);
    } else if (SHOWCASE_SCREENS.includes(screen) && this.session === null) {
      // The showcase: the car you would drive, idling on the otherwise empty
      // half of the front screen. Only without a session — with one, the menu
      // is an overlay and the paused match behind it IS the backdrop (and owns
      // the WebGL context this turntable would otherwise be a second of).
      //
      // `welcome` gets it too. It is the first thing anyone sees, three rows
      // tall, and a landing screen that is three rows and a void undersells
      // the game badly.
      this.extraEl.innerHTML =
        `<div class="rw-garage-preview rw-showcase" data-garage-preview></div>` +
        `<div class="rw-showcase-tag">${screen === 'welcome' ? 'Vroomball' : 'Your car'}</div>`;
    } else {
      this.extraEl.innerHTML = '';
    }
  }

  /**
   * The tally under a finished run: the drill, what was hit, and one line
   * about it.
   *
   * The same card grid the match setup uses, deliberately — a drill result and
   * a match result are the same KIND of thing to a player, and inventing a
   * second panel style for it would make the front end look like two products.
   */
  private drillResultMarkup(): string {
    const summary = this.model.drillSummary;
    if (summary === null) return '';
    const cell = (key: string, value: string): string =>
      `<div class="rw-card-cell"><div class="rw-card-key">${esc(key)}</div>` +
      `<div class="rw-card-val">${esc(value)}</div></div>`;
    const rate = summary.attempts === 0 ? 0 : Math.round((summary.hits / summary.attempts) * 100);
    return (
      `<div class="rw-card">${cell('Drill', summary.name)}` +
      cell('Hit', `${summary.hits} / ${summary.attempts}`) +
      cell('Rate', `${rate}%`) +
      `</div><div class="rw-note-line">${esc(drillVerdict(summary))}</div>`
    );
  }

  private matchCardMarkup(): string {
    const setup = this.model.settings.setup;
    const cell = (key: string, value: string): string =>
      `<div class="rw-card-cell"><div class="rw-card-key">${esc(key)}</div>` +
      `<div class="rw-card-val">${esc(value)}</div></div>`;
    // Derived, not a mode -> label table: 'bot-showcase' is a 1v1 and used to
    // read "2v2" here because the old test was `mode === 'duel'`.
    const perTeam = teamSizeForMode(setup.mode);
    const roster = botRosterFor(setup);
    const bots = roster.configs.length;
    // Free play is not an NvN and has no bots, no overtime and no clock, so
    // the card says what it IS rather than printing "1v1 · 0 bots · Off" and
    // leaving the player to work out that three of the four cells are noise.
    if (setup.mode === 'freeplay') {
      return (
        `<div class="rw-card">${cell('Format', 'Free Play')}` +
        cell('Team', TEAM_NAMES[setup.team]) +
        cell('Bots', 'None') +
        cell('Clock', 'Unlimited') +
        `</div>`
      );
    }
    return (
      `<div class="rw-card">${cell('Format', `${perTeam}v${perTeam}`)}` +
      cell('Team', TEAM_NAMES[setup.team]) +
      cell('Bots', `${bots} · ${difficultyLabel(setup.difficulty)}`) +
      cell('Overtime', setup.overtime ? 'On' : 'Off') +
      `</div>`
    );
  }

  private scoreboardMarkup(): string {
    const result = this.model.result;
    if (!result) return '';
    const [blue, orange] = result.score;
    const tag =
      result.winner === null
        ? 'Draw'
        : result.winner === result.playerTeam
          ? 'Victory'
          : 'Defeat';

    const goals = result.goals
      .map(
        g =>
          `<div class="rw-goal"><div class="rw-goal-clock">${clockLabel(g.clockRemaining)}</div>` +
          `<div class="rw-goal-team" style="color:${TEAM_HEX[g.team]}">${TEAM_NAMES[g.team]}</div>` +
          `<div class="rw-goal-speed">${Math.round(g.speed)} uu/s</div></div>`
      )
      .join('');

    return (
      `<div class="rw-final">` +
      `<div class="rw-final-team"><div class="rw-final-name" style="color:${TEAM_HEX[0]}">Blue</div>` +
      `<div class="rw-final-score">${blue}</div></div>` +
      `<div class="rw-final-dash">—</div>` +
      `<div class="rw-final-team right"><div class="rw-final-name" style="color:${TEAM_HEX[1]}">Orange</div>` +
      `<div class="rw-final-score">${orange}</div></div></div>` +
      `<div class="rw-final-tag">${tag}${result.overtime ? ' · after overtime' : ''}</div>` +
      (goals ? `<div class="rw-goals">${goals}</div>` : '')
    );
  }

  private renderLegend(screen: Screen): void {
    const prompt = screen === 'bind' || screen === 'text';
    const keyboard =
      screen === 'bind'
        ? '<em>Esc</em> Cancel &nbsp; <em>Backspace</em> Clear'
        : screen === 'text'
          ? '<em>Enter</em> Accept &nbsp; <em>Backspace</em> Delete &nbsp; <em>Esc</em> Cancel'
          : '<em>↑↓</em> Navigate &nbsp; <em>←→</em> Adjust &nbsp; <em>Enter</em> Select &nbsp; <em>Esc</em> Back';
    // Both prompts are keyboard-only, and for the same reason: a gamepad has no
    // letters. Saying so beats a pad that silently does nothing.
    const gamepad = prompt
      ? 'Keyboard only'
      : '<em>D-Pad</em> Navigate &nbsp; <em>A</em> Select &nbsp; <em>B</em> Back &nbsp; <em>Start</em> Pause';
    this.legendEl.innerHTML = `<div>${keyboard}</div><div class="gap"></div><div>${gamepad}</div>`;
  }

  private renderModal(): void {
    const bind = this.model.bindTarget;
    const text = this.model.textTarget;
    const open =
      (this.model.screen === 'bind' && bind !== null) ||
      (this.model.screen === 'text' && text !== null);
    this.modalEl.hidden = !open;
    if (!open) return;

    if (this.model.screen === 'text' && text !== null) {
      const spec = TEXT_FIELDS[text.field];
      this.modalEl.dataset.kind = 'text';
      this.modalAction.textContent = spec.label;
      // The caret is a rendered block, not a real one: there is no `<input>`
      // here, and faking the caret is far less code than owning a focused field
      // that the pad loop and the game's key handler both have to route around.
      // A masked field (the password) renders one dot per character.
      this.modalTitle.innerHTML =
        `<span class="rw-typed">${esc(displayTextValue(this.model.textBuffer, spec))}</span><span class="rw-caret"></span>`;
      this.modalHint.textContent = `${spec.hint}  ·  Enter accepts  ·  Esc cancels`;
      return;
    }

    if (bind === null) return;
    this.modalEl.dataset.kind = 'bind';
    const row = BINDING_ROWS.find(r => r.action === bind.action);
    this.modalAction.textContent = `${row?.label ?? bind.action} · Slot ${bind.slot + 1}`;
    this.modalTitle.textContent = 'Press a key';
    this.modalHint.textContent = 'Esc cancels · Backspace clears';
  }

  // -------------------------------------------------------------------------

  dispose(): void {
    if (this.padLoop !== null) cancelAnimationFrame(this.padLoop);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('paste', this.onPaste);
    // Before the session: the socket is what holds a seat open on the server,
    // and a hot reload that skips this leaves a ghost in every room it visited.
    this.net.dispose();
    // Also a WebGL context, and the last thing to hold one on a hot reload.
    this.preview?.dispose();
    this.preview = null;
    this.disposeWatch();
    this.disposeSession();
    // Terminates the rollout workers. A worker survives its page's DOM, so
    // skipping this leaves N threads pinning cores after a hot reload.
    this.training.dispose();
    this.root.remove();
    this.styleEl.remove();
  }
}
